import React, { useState, useEffect, useRef, useCallback } from 'react';
import { findPerson, getDailyBriefing } from '../../services/sash-service';
import { jobService } from '../../services/job-service';
import { customerService } from '../../services/customer-service';
import { useAuth } from '../../contexts/AuthContext';
import { router } from '../../router';
import './SashWidget.css';

// Quick Quote (create + edit) is a focused work page — Sash doesn't show up
// there at all, so it never competes with the line-item editor or the TWP
// Price Calculator that also floats on those screens.
// The Archive search is full-page too, so Sash stays off it.
// The Website pages (dashboard, leads, requests, design studio) fill the screen, so Sash stays off them too.
const HIDDEN_PATH_PREFIXES = ['/estimates/new', '/estimates/edit/', '/archive', '/website'];

// SashWidget mounts as a sibling of <RouterProvider> in main.jsx, not inside
// the routed tree, so useLocation() isn't available. `router` (from
// createBrowserRouter) is a plain object with .subscribe(), which works
// regardless of where the component sits in the React tree.
function useIsHiddenPage() {
  const [pathname, setPathname] = useState(router.state.location.pathname);
  useEffect(() => {
    const unsubscribe = router.subscribe((state) => setPathname(state.location.pathname));
    return unsubscribe;
  }, []);
  return HIDDEN_PATH_PREFIXES.some((p) => pathname.startsWith(p));
}

// How long each rotating greeting line stays up before advancing (~5s reads
// comfortably; shorter feels rushed).
const ROTATE_MS = 5500;

// ─── Audio ────────────────────────────────────────────────────────────────────

function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    // Two-note rising ding: A5 → C6
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1046.5, ctx.currentTime + 0.13);
    gain.gain.setValueAtTime(0.22, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.65);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.65);
  } catch {
    // Autoplay blocked or AudioContext unavailable — skip silently
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(date) {
  if (!date) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Same as formatDate but without the year — for near-term dates like "Sep 8". */
function formatDateShort(date) {
  if (!date) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** "Good morning" / "Good afternoon" / "Good evening" for the current hour. */
function timeGreeting(now = new Date()) {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/** First token of a display name. Ignores bare email addresses. */
function firstNameOf(...candidates) {
  for (const c of candidates) {
    if (!c || typeof c !== 'string') continue;
    if (c.includes('@')) continue; // skip emails — no reliable first name
    const first = c.trim().split(/\s+/)[0];
    if (first) return first;
  }
  return '';
}

// Accent styling for the scannable bits (dates / counts) in the greeting.
const GREET_ACCENT = {
  color: 'var(--brand-brown)',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};
const A = (txt) => <span style={GREET_ACCENT}>{txt}</span>;

/**
 * One short line for a day. Exactly one job → name it, with the town/city
 * (job.name is the free-text location field) when we also have a customer name.
 * Multiple → just the count.
 */
function dayLine(when, jobs) {
  const n = jobs?.length || 0;
  if (n === 0) return <>No jobs scheduled {when}</>;
  if (n === 1) {
    const job = jobs[0];
    const cust = (job?.customerName || '').trim();
    const town = (job?.name || '').trim();
    const label = cust || town || 'Unnamed job';
    return <>1 job {when}: {A(label)}{cust && town ? ` — ${town}` : ''}</>;
  }
  return <>{A(n)} jobs scheduled {when}</>;
}

/**
 * The rotating lines shown under "Good afternoon, Noah!". One idea per slide,
 * kept short so the bubble stays small. Order: intro, search hint, sold-out,
 * scheduled-through, today, tomorrow, booked-status hint, then loops. The lines
 * describe what Sash can actually do (customer lookup plus schedule status),
 * not "ask me anything". It is not a general chatbot.
 */
function buildGreetingLines(briefing) {
  const lines = [
    <>Hi, I&apos;m Sash, your TWP virtual assistant.</>,
    <>Search a customer by name, phone, or email</>,
  ];

  if (briefing) {
    const sold = formatDate(briefing.soldOutThrough);
    const sched = formatDateShort(briefing.scheduledThrough);
    if (sold) lines.push(<>Sold out through {A(sold)}</>);
    if (sched) lines.push(<>Scheduled through {A(sched)}</>);
    lines.push(dayLine('today', briefing.todayJobs));
    lines.push(dayLine('tomorrow', briefing.tomorrowJobs));
  }

  lines.push(<>Ask &ldquo;how far out are we booked?&rdquo; for schedule status</>);
  return lines;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

/** 4-pane window with a subtle friendly face in the top-left pane */
function WindowIcon({ size = 28 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Outer frame */}
      <rect x="2" y="2" width="28" height="28" rx="4.5"
        stroke="currentColor" strokeWidth="2.5" fill="none" />
      {/* Horizontal divider */}
      <line x1="2" y1="16" x2="30" y2="16"
        stroke="currentColor" strokeWidth="2" />
      {/* Vertical divider */}
      <line x1="16" y1="2" x2="16" y2="30"
        stroke="currentColor" strokeWidth="2" />
      {/* Eyes in top-left pane */}
      <circle cx="8.5" cy="9.5" r="1.1" fill="currentColor" />
      <circle cx="12.5" cy="9.5" r="1.1" fill="currentColor" />
      {/* Smile */}
      <path d="M8 12.5 Q10.5 14.5 13 12.5"
        stroke="currentColor" strokeWidth="1.1" fill="none" strokeLinecap="round" />
    </svg>
  );
}

/** Small document icon for PDF links */
function DocIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" rx="1.5"
        stroke="currentColor" strokeWidth="1.2" />
      <line x1="3.5" y1="4"   x2="8.5" y2="4"   stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      <line x1="3.5" y1="6.5" x2="8.5" y2="6.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      <line x1="3.5" y1="9"   x2="6"   y2="9"   stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

// ─── Auth gate ────────────────────────────────────────────────────────────────
// SashWidget is mounted globally in main.jsx (outside the router) so it floats
// over every screen. It must NOT appear until the user is fully authenticated,
// has a loaded profile, and is past the PIN gate — the same checks
// ProtectedRoute runs. It's also scoped to the office/management roles that use
// the scheduler (MANAGER_ROLES in router.jsx), so it never shows on the login
// screen or the installer / shop-worker portals. Gating the mount here (rather
// than inside the component) means the greeting + chime effects only ever run
// for a signed-in scheduler user.

const SASH_ROLES = ['manager', 'installManager', 'sales', 'officeManager'];

export default function SashWidget() {
  const { currentUser, userProfile } = useAuth() || {};
  if (!currentUser || !userProfile) return null;
  // Match ProtectedRoute: users with no role set are treated as 'manager'.
  const role = userProfile.role || 'manager';
  if (!SASH_ROLES.includes(role)) return null;
  return (
    <SashAssistant
      firstName={firstNameOf(userProfile.name, userProfile.displayName, currentUser.displayName)}
    />
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

function SashAssistant({ firstName }) {
  const [isOpen, setIsOpen]           = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [briefing, setBriefing]       = useState(null);
  const [searchTerm, setSearchTerm]   = useState('');
  const [results, setResults]         = useState(null);  // null = not yet searched
  const [isSearching, setIsSearching] = useState(false);
  const [entered, setEntered]         = useState(false);  // one-time attention pulse
  const [msgIdx, setMsgIdx]           = useState(0);       // rotating greeting line
  const searchTimeout = useRef(null);
  const inputRef      = useRef(null);
  const isHidden       = useIsHiddenPage();

  // Play the attention pulse once shortly after mount, then let it settle.
  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 1400);
    return () => clearTimeout(t);
  }, []);

  // Greet on every load. This component only mounts once the user is fully
  // authenticated (see the SashWidget gate above), so the chime fires on every
  // reload for a signed-in user and never on the login screen. The time-of-day
  // greeting needs no data — show the bubble immediately; the schedule-status
  // lines fill in once jobService has its first non-empty snapshot (its
  // listener isn't started until JobProvider mounts inside ProtectedRoute).
  useEffect(() => {
    console.log('[Sash] mounted — showing greeting');
    setShowGreeting(true);
    playChime();

    let gotBriefing = false;
    const unsub = jobService.subscribe((jobs) => {
      if (gotBriefing || jobs.length === 0) return;
      gotBriefing = true;
      const data = getDailyBriefing();
      console.log('[Sash] briefing ready —', jobs.length, 'jobs:', data);
      setBriefing(data);
    });

    return () => unsub();
  }, []);

  // State monitor — remove once rendering bug is resolved
  useEffect(() => {
    console.log('[Sash state]', {
      isOpen,
      isSearching,
      results: results === null
        ? 'null'
        : Array.isArray(results)
          ? `array(${results.length})`
          : results?.type ?? 'unknown',
    });
  }, [isOpen, isSearching, results]);

  // Focus search input when panel opens; clear state when it closes
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    setSearchTerm('');
    setResults(null);
  }, [isOpen]);

  // Keywords that show the daily briefing instead of a customer search
  const BRIEFING_RE = /sold.?out|scheduled.?through|booked.?through|sold.?through|how.?far/i;

  // Debounced search (350 ms)
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    clearTimeout(searchTimeout.current);

    if (!term.trim()) {
      setResults(null);
      setIsSearching(false);
      return;
    }

    // Briefing keyword shortcut — no async needed
    if (BRIEFING_RE.test(term.trim())) {
      const data = getDailyBriefing();
      setResults({ type: 'briefing', ...data });
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const custCount = customerService.getCustomers().length;
        const jobCount = jobService.getJobs().length;
        console.log('[Sash] search term:', JSON.stringify(term.trim()),
          '| customers loaded:', custCount, '| jobs loaded:', jobCount);
        // Jobs are the primary lookup source now — only bail if nothing at all
        // has loaded yet.
        if (custCount === 0 && jobCount === 0) {
          setResults({ type: 'not_ready' });
          return;
        }
        const data = await findPerson(term.trim());
        console.log('[Sash] findPerson results:', data.length,
          data.map(r => `${r.customer.name} (${r.jobOrders.length} orders)`));
        setResults(data);
      } catch (err) {
        console.error('[Sash] search error:', err);
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 350);
  }, []);

  const dismissGreeting = () => setShowGreeting(false);
  const togglePanel = () => {
    setShowGreeting(false);
    setIsOpen((prev) => !prev);
  };

  // ─── Greeting ──────────────────────────────────────────────────────────────

  const hello = firstName
    ? `${timeGreeting()}, ${firstName}!`
    : `${timeGreeting()}!`;

  // Rotating info lines shown under the greeting; recomputed each render (cheap).
  const greetingLines = buildGreetingLines(briefing);
  const currentLine = greetingLines[msgIdx % greetingLines.length];

  // Auto-advance through the lines, looping.
  useEffect(() => {
    const count = greetingLines.length;
    if (count <= 1) { setMsgIdx(0); return; }
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % count), ROTATE_MS);
    return () => clearInterval(t);
  }, [greetingLines.length]);

  // ─── Inline styles ─────────────────────────────────────────────────────────

  const s = {
    root: {
      position: 'fixed',
      bottom: 96,   // clear the bottom nav / mobile toolbar
      right: 24,
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: 8,
      fontFamily: 'inherit',
    },

    // ── Mascot button ──
    mascot: {
      width: 58,
      height: 58,
      borderRadius: 18,
      border: '1px solid var(--sash-ring)',
      background: 'var(--sidebar-brand-gradient)',
      color: '#fff',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow:
        '0 8px 24px var(--bg-card-shadow), 0 0 0 6px var(--sash-halo)',
      flexShrink: 0,
      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      animation: isOpen
        ? 'none'
        : entered
          ? 'sash-glow 4.5s ease-in-out infinite, sash-idle 3.5s ease-in-out infinite'
          : 'sash-attention 1.4s ease-out both',
    },

    // ── Speech bubble ──
    speechOuter: {
      marginBottom: 4,
    },
    bubble: {
      position: 'relative',
      background: 'var(--bg-card)',
      border: '1px solid var(--glass-border)',
      borderRadius: 12,
      padding: '10px 36px 10px 14px',
      boxShadow: '0 4px 16px var(--bg-card-shadow)',
      backdropFilter: 'blur(10px)',
      maxWidth: 260,
      animation: 'sash-speech-in 0.3s ease',
    },
    bubbleGreet: {
      fontSize: 13,
      fontWeight: 700,
      color: 'var(--text-primary)',
      margin: '0 0 3px',
      lineHeight: 1.4,
    },
    bubbleText: {
      fontSize: 13,
      color: 'var(--text-secondary)',
      margin: 0,
      lineHeight: 1.5,
      minHeight: 20,
    },
    bubbleClose: {
      position: 'absolute',
      top: 7,
      right: 9,
      background: 'none',
      border: 'none',
      color: 'var(--text-tertiary)',
      cursor: 'pointer',
      fontSize: 16,
      lineHeight: 1,
      padding: '2px 4px',
      borderRadius: 4,
    },
    // Triangle tail (outer = border colour, inner covers it leaving just the edge)
    tailOuter: {
      position: 'absolute',
      bottom: -9,
      right: 20,
      width: 0,
      height: 0,
      borderLeft:  '9px solid transparent',
      borderRight: '9px solid transparent',
      borderTop:   '9px solid var(--glass-border)',
    },
    tailInner: {
      position: 'absolute',
      bottom: -7,
      right: 20,
      width: 0,
      height: 0,
      borderLeft:  '9px solid transparent',
      borderRight: '9px solid transparent',
      borderTop:   '9px solid var(--bg-card)',
    },

    // ── Panel ──
    panel: {
      width: 320,
      background: 'var(--bg-card)',
      border: '1px solid var(--glass-border)',
      borderRadius: 16,
      boxShadow: '0 8px 32px var(--bg-card-shadow)',
      backdropFilter: 'blur(12px)',
      overflow: 'hidden',
      animation: 'sash-panel-in 0.2s ease',
    },
    panelHeader: {
      padding: '12px 16px',
      borderBottom: '1px solid var(--border-default)',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    },
    panelIcon: {
      color: 'var(--brand-brown)',
      flexShrink: 0,
      display: 'flex',
    },
    panelTitles: {
      flex: 1,
    },
    panelTitle: {
      fontSize: 14,
      fontWeight: 700,
      color: 'var(--text-primary)',
      margin: 0,
      lineHeight: 1.2,
    },
    panelSubtitle: {
      fontSize: 11,
      color: 'var(--text-tertiary)',
      margin: 0,
      lineHeight: 1.4,
    },
    panelClose: {
      background: 'none',
      border: 'none',
      color: 'var(--text-tertiary)',
      cursor: 'pointer',
      fontSize: 20,
      lineHeight: 1,
      padding: '2px 4px',
      borderRadius: 4,
    },

    // ── Search input ──
    searchInput: {
      display: 'block',
      width: '100%',
      padding: '10px 14px',
      border: 'none',
      borderBottom: '1px solid var(--border-default)',
      background: 'var(--input-bg)',
      color: 'var(--input-text)',
      fontSize: 13,
      outline: 'none',
      boxSizing: 'border-box',
    },

    // ── Results area ──
    resultsArea: {
      maxHeight: 360,
      overflowY: 'auto',
      padding: '6px 0',
    },
    empty: {
      padding: '24px 16px',
      textAlign: 'center',
      color: 'var(--text-tertiary)',
      fontSize: 13,
    },

    // ── Customer block ──
    customerBlock: (isLast) => ({
      padding: '10px 16px',
      borderBottom: isLast ? 'none' : '1px solid var(--border-default)',
    }),
    customerName: {
      fontSize: 13,
      fontWeight: 600,
      color: 'var(--text-primary)',
      margin: '0 0 2px',
    },
    customerMeta: {
      fontSize: 11,
      color: 'var(--text-secondary)',
      margin: '0 0 6px',
    },
    noOrders: {
      fontSize: 12,
      color: 'var(--text-tertiary)',
      margin: 0,
    },

    // ── Order row ──
    orderItem: {
      marginBottom: 6,
    },
    orderRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      flexWrap: 'wrap',
      marginBottom: 3,
    },
    contractNum: {
      fontSize: 12,
      fontWeight: 600,
      color: 'var(--brand-brown)',
    },
    statusPill: {
      fontSize: 10,
      padding: '2px 6px',
      borderRadius: 99,
      background: 'var(--status-pill-default-bg)',
      color: 'var(--status-pill-default-text)',
    },

    // ── PDF links ──
    pdfLinks: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '4px 8px',
    },
    pdfLink: {
      fontSize: 11,
      color: 'var(--brand-brown)',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
    },
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  // Hidden entirely on Quick Quote (create/edit) — all hooks above still ran
  // unconditionally this render, so bailing out here is safe.
  if (isHidden) return null;

  return (
    <div className="sash-root" style={s.root}>

      {/* Speech bubble greeting (every load) */}
      {showGreeting && (
        <div style={s.speechOuter}>
          <div style={s.bubble}>
            <p style={s.bubbleGreet}>{hello}</p>
            <p key={msgIdx} style={{ ...s.bubbleText, animation: 'sash-speech-in 0.35s ease' }}>
              {currentLine}
            </p>
            <button
              style={s.bubbleClose}
              onClick={dismissGreeting}
              aria-label="Dismiss greeting"
            >
              ×
            </button>
            {/* Down-pointing tail toward mascot */}
            <div style={s.tailOuter} />
            <div style={s.tailInner} />
          </div>
        </div>
      )}

      {/* Expanded search panel */}
      {isOpen && (
        <div style={s.panel} role="dialog" aria-label="Sash — TWP Virtual Assistant">

          {/* Header */}
          <div style={s.panelHeader}>
            <div style={s.panelIcon}>
              <WindowIcon size={26} />
            </div>
            <div style={s.panelTitles}>
              <p style={s.panelTitle}>Sash</p>
              <p style={s.panelSubtitle}>TWP Virtual Assistant</p>
            </div>
            <button
              style={s.panelClose}
              onClick={() => setIsOpen(false)}
              aria-label="Close Sash"
            >
              ×
            </button>
          </div>

          {/* Search */}
          <input
            ref={inputRef}
            style={s.searchInput}
            type="search"
            placeholder="Name, phone, or email…"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            autoComplete="off"
          />

          {/* Results */}
          <div style={s.resultsArea}>
            {isSearching && (
              <div style={s.empty}>Searching…</div>
            )}

            {!isSearching && results === null && (
              <div style={s.empty}>
                Search a customer by name, phone, or email.
                Or ask &ldquo;how far out are we booked?&rdquo;
              </div>
            )}

            {/* Customer service not yet loaded */}
            {!isSearching && results?.type === 'not_ready' && (
              <div style={s.empty}>Still loading customers — try again in a moment</div>
            )}

            {/* Briefing keyword result */}
            {!isSearching && results?.type === 'briefing' && (
              <div style={{ padding: '14px 16px' }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Schedule Status
                </p>
                {results.soldOutThrough ? (
                  <p style={{ fontSize: 13, color: 'var(--text-primary)', margin: '0 0 6px' }}>
                    Sold out through <strong>{formatDate(results.soldOutThrough)}</strong>
                  </p>
                ) : (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 6px' }}>
                    Sold-out date unavailable
                  </p>
                )}
                {results.scheduledThrough && (
                  <p style={{ fontSize: 13, color: 'var(--text-primary)', margin: '0 0 6px' }}>
                    Scheduled through <strong>{formatDateShort(results.scheduledThrough)}</strong>
                  </p>
                )}
                <p style={{ fontSize: 13, color: 'var(--text-primary)', margin: 0 }}>
                  <strong>{results.todayJobCount}</strong>{' '}
                  {results.todayJobCount === 1 ? 'job' : 'jobs'} scheduled today
                </p>
              </div>
            )}

            {!isSearching && Array.isArray(results) && results.length === 0 && (
              <div style={s.empty}>No matches found</div>
            )}

            {!isSearching && Array.isArray(results) && results.map(({ customer, jobOrders }, idx) => {
              console.log('[Sash render row]', idx, customer?.name, 'id:', customer?.id, 'orders:', jobOrders?.length);
              return (
              <div
                key={customer.id ?? idx}
                style={s.customerBlock(idx === results.length - 1)}
              >
                <p style={s.customerName}>{customer.name}</p>

                {(customer.phone || customer.email) && (
                  <p style={s.customerMeta}>
                    {[customer.phone, customer.email].filter(Boolean).join(' · ')}
                  </p>
                )}

                {customer.address && (
                  <p style={s.customerMeta}>{customer.address}</p>
                )}

                {jobOrders.length === 0 ? (
                  <p style={s.noOrders}>No job orders</p>
                ) : (
                  jobOrders.map(({ order, documents }) => (
                    <div key={order.id} style={s.orderItem}>
                      <div style={s.orderRow}>
                        <span style={s.contractNum}>#{order.contractNumber}</span>
                        <span style={s.statusPill}>
                          {order.statusLabel || order.status}
                        </span>
                      </div>

                      {documents.length > 0 && (
                        <div style={s.pdfLinks}>
                          {documents.map(({ key, label, url }) => (
                            <a
                              key={key + url}
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              style={s.pdfLink}
                            >
                              <DocIcon />
                              {label}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Mascot bubble */}
      <button
        className="sash-mascot"
        style={s.mascot}
        onClick={togglePanel}
        aria-label={isOpen ? 'Close Sash' : 'Open Sash assistant'}
        title="Sash — TWP Virtual Assistant"
      >
        <WindowIcon size={30} />
      </button>

    </div>
  );
}
